// Deterministic replays for Lesson 4.12 ("The Loop Has Names: ReAct, Plan, Reflect"). The
// steps, thoughts, plans, verdicts, token counts and tool_choice replies below were recorded on
// 9 October 2026 with zai.glm-5 through the gateway, the Workshop 4.5 assistant with its
// thirteen tools and the module dataset with the notes ingested. Nothing here calls a model.

export type Tone = 'neutral' | 'success' | 'warning';
export interface Summary {
  message: string;
  tone: Tone;
}

export const RELEASE_QUESTION = 'Who owns the release note, and is the room booked?';
export const HALF_ANSWER =
  'The notes do not say who owns the release note. The main hall is booked [5.1].';
export const SEARCH_CALL = 'search_notes("release note owner room booked")';

// --- pattern-react: a thought before each call ---------------------------------------------

export const LOOPS = ['plain', 'thought'] as const;
export type Loop = (typeof LOOPS)[number];
export const loopLabels: Record<Loop, string> = {
  plain: 'plain',
  thought: 'with a thought',
};
export const toLoop = (value: string): Loop => LOOPS.find((id) => id === value) ?? 'plain';
export interface LoopStep {
  /** The one-sentence thought before the call, or null on the plain loop. */
  thought: string | null;
  action: string;
}
export const REACT_THOUGHT =
  'I need to search the notes for information about the release note ownership and room booking.';
export const THOUGHT_EXTRA = 'One trial of three added "Ravi owns the release note draft [5.1]".';
export interface LoopRun {
  steps: readonly LoopStep[];
  promptTokens: number;
  answer: string;
  /** What a reader of the trace sees once the run is shown. */
  message: string;
}
export const loopRuns: Record<Loop, LoopRun> = {
  plain: {
    steps: [
      { thought: null, action: SEARCH_CALL },
      { thought: null, action: `answer: "${HALF_ANSWER}"` },
    ],
    promptTokens: 4_515,
    answer: HALF_ANSWER,
    message: 'The loop searched the notes once and never read the task list.',
  },
  thought: {
    steps: [
      { thought: REACT_THOUGHT, action: SEARCH_CALL },
      { thought: null, action: `answer: "${HALF_ANSWER}"` },
    ],
    promptTokens: 4_579,
    answer: HALF_ANSWER,
    message: 'The thought named the notes before any call, and the search stayed the same.',
  },
};
export const thoughtText = (step: LoopStep) => step.thought ?? '(none)';
export const callCount = (run: LoopRun) => run.steps.length;
export const tokensText = (tokens: number) => tokens.toLocaleString('en-US');
export interface ReactState {
  loop: Loop;
  run: boolean;
}
export const initialReact = (): ReactState => ({ loop: 'plain', run: false });
export type ReactAction = { type: 'loop'; value: Loop } | { type: 'run' } | { type: 'reset' };
export function reactTransition(state: ReactState, action: ReactAction): ReactState {
  switch (action.type) {
    case 'reset':
      return initialReact();
    case 'loop':
      return { loop: action.value, run: false };
    case 'run':
      return { ...state, run: true };
  }
}
export const REACT_START_MESSAGE = 'Choose a loop, then press Run the question.';
export const REACT_REFINEMENT = 'Refinement: a thought is for the reader of the trace, not a fix.';
export function reactSummary(state: ReactState): Summary {
  if (!state.run) return { tone: 'neutral', message: REACT_START_MESSAGE };
  return { tone: 'warning', message: loopRuns[state.loop].message };
}

// --- pattern-plan: a planner call, then the loop follows the plan ------------------------

export const PLANNER_VIEWS = ['names', 'descriptions', 'rule'] as const;
export type PlannerView = (typeof PLANNER_VIEWS)[number];
export const plannerViewLabels: Record<PlannerView, string> = {
  names: 'tool names',
  descriptions: 'tool descriptions',
  rule: 'descriptions and the tasks-against-notes rule',
};
export const toPlannerView = (value: string): PlannerView =>
  PLANNER_VIEWS.find((id) => id === value) ?? 'names';
export interface PlanStep {
  plan: string;
  call: string;
}
export const TASKS_RULE =
  'The task list is the record of ownership and current work; the notes are what people wrote.';
export const NOT_RECORDED = 'not measured';
export interface PlanRun {
  /** What the planner was given, as the learner reads it. */
  sees: string;
  plannerPromptTokens: number | null;
  steps: readonly PlanStep[];
  /** What the loop did with the plan. */
  loop: string;
  message: string;
}
export const planRuns: Record<PlannerView, PlanRun> = {
  names: {
    sees: 'the question and the thirteen tool names',
    plannerPromptTokens: 96,
    steps: [
      { plan: '1. search_notes with query "release note"', call: 'search_notes("release note")' },
      { plan: '2. search_notes with query "room booked"', call: 'search_notes("room booked")' },
    ],
    loop: `Two searches, 5,077 prompt tokens, then the same half answer: "${HALF_ANSWER}"`,
    message: 'Two searches of the notes were planned, and the task list was never in the plan.',
  },
  descriptions: {
    sees: 'the question and the thirteen tools with their full descriptions',
    plannerPromptTokens: 670,
    steps: [
      { plan: '1. search_notes', call: 'search_notes(…)' },
      { plan: '2. search_notes', call: 'search_notes(…)' },
      { plan: '3. search_notes', call: 'search_notes(…)' },
    ],
    loop: 'Three searches of the notes were planned, and the task list is not in the plan.',
    message: 'Descriptions added words and changed nothing: three searches of the notes.',
  },
  rule: {
    sees: `the question, the tool descriptions and the rule: "${TASKS_RULE}"`,
    plannerPromptTokens: null,
    steps: [],
    loop: 'Not measured here. A planner that sees the rule is what you build next, and the thirty tasks measure it.',
    message:
      'No run with the rule exists in this graphic; the thirty tasks measure it once you build it.',
  },
};
export const plannerTokensText = (run: PlanRun) =>
  run.plannerPromptTokens === null ? NOT_RECORDED : tokensText(run.plannerPromptTokens);
export const stepsPlannedText = (run: PlanRun) =>
  run.steps.length === 0 ? NOT_RECORDED : String(run.steps.length);
export const listTasksPlanned = (run: PlanRun) =>
  run.steps.length === 0
    ? NOT_RECORDED
    : run.steps.some((step) => step.plan.includes('list_tasks'))
      ? 'yes'
      : 'no';
export interface PlanState {
  view: PlannerView;
  run: boolean;
}
export const initialPlan = (): PlanState => ({ view: 'names', run: false });
export type PlanAction = { type: 'view'; value: PlannerView } | { type: 'run' } | { type: 'reset' };
export function planTransition(state: PlanState, action: PlanAction): PlanState {
  switch (action.type) {
    case 'reset':
      return initialPlan();
    case 'view':
      return { view: action.value, run: false };
    case 'run':
      return { ...state, run: true };
  }
}
export const PLAN_START_MESSAGE = 'Choose what the planner sees, then press Plan, then run.';
export const PLAN_REFINEMENT =
  "Refinement: the planner needs the loop's rules, not only its tools.";
export function planSummary(state: PlanState): Summary {
  if (!state.run) return { tone: 'neutral', message: PLAN_START_MESSAGE };
  return {
    tone: state.view === 'rule' ? 'neutral' : 'warning',
    message: planRuns[state.view].message,
  };
}

// --- pattern-reflect: a second call that checks the answer -----------------------------

export const DRAFTS = ['count', 'dodge'] as const;
export type Draft = (typeof DRAFTS)[number];
export const draftLabels: Record<Draft, string> = {
  count: 'the right count',
  dodge: 'the dodging release-note answer',
};
export const toDraft = (value: string): Draft => DRAFTS.find((id) => id === value) ?? 'count';
export const COUNT_QUESTION = 'How many Apollo tasks are open and who owns them?';
export const COUNT_DRAFT = 'Six tasks are open: one assigned to Ravi, five with no owner.';
export const TASK_LIST =
  'the Apollo task list, 15 tasks, task 9 "Write the release note" assigned to Ravi';
export interface Reflection {
  question: string;
  draft: string;
  /** The recorded verdict with the task list in front of the checker. */
  verdict: string;
  promptTokens: number | null;
  completionTokens: number | null;
  message: string;
}
export const reflections: Record<Draft, Reflection> = {
  count: {
    question: COUNT_QUESTION,
    draft: COUNT_DRAFT,
    verdict: 'CORRECT',
    promptTokens: 245,
    completionTokens: 3,
    message: 'The draft matched the task list, and the checker said CORRECT in three tokens.',
  },
  dodge: {
    question: RELEASE_QUESTION,
    draft: HALF_ANSWER,
    verdict: 'CORRECT',
    promptTokens: null,
    completionTokens: null,
    message:
      'The draft did not contradict the task list, so the checker said CORRECT and missed the dodge.',
  },
};
/** What the checker could read: the question and the draft, and the task list when given. */
export const checkerSees = (draft: Draft, taskList: boolean): readonly string[] => {
  const reflection = reflections[draft];
  const seen = [`the question: "${reflection.question}"`, `the draft: "${reflection.draft}"`];
  return taskList ? [...seen, TASK_LIST] : seen;
};
export const verdictText = (draft: Draft, taskList: boolean) =>
  taskList ? reflections[draft].verdict : NOT_RECORDED;
export const reflectTokensText = (draft: Draft, taskList: boolean) => {
  const reflection = reflections[draft];
  if (!taskList || reflection.promptTokens === null || reflection.completionTokens === null)
    return NOT_RECORDED;
  return `${tokensText(reflection.promptTokens)} prompt, ${reflection.completionTokens} completion`;
};
export interface ReflectState {
  draft: Draft;
  taskList: boolean;
  run: boolean;
}
/** Both recorded verdicts had the task list in front of the checker, so it starts given. */
export const initialReflect = (): ReflectState => ({ draft: 'count', taskList: true, run: false });
export type ReflectAction =
  | { type: 'draft'; value: Draft }
  | { type: 'toggle-task-list' }
  | { type: 'run' }
  | { type: 'reset' };
export function reflectTransition(state: ReflectState, action: ReflectAction): ReflectState {
  switch (action.type) {
    case 'reset':
      return initialReflect();
    case 'draft':
      return { ...state, draft: action.value, run: false };
    case 'toggle-task-list':
      return { ...state, taskList: !state.taskList, run: false };
    case 'run':
      return { ...state, run: true };
  }
}
export const REFLECT_START_MESSAGE = 'Choose an answer to check, then press Reflect.';
export const REFLECT_NO_LIST_MESSAGE =
  'Without the task list the checker has only the draft, and no such run exists in this graphic.';
export const REFLECT_REFINEMENT = 'Refinement: reflection catches contradiction, not avoidance.';
export function reflectSummary(state: ReflectState): Summary {
  if (!state.run) return { tone: 'neutral', message: REFLECT_START_MESSAGE };
  if (!state.taskList) return { tone: 'neutral', message: REFLECT_NO_LIST_MESSAGE };
  return {
    tone: state.draft === 'count' ? 'success' : 'warning',
    message: reflections[state.draft].message,
  };
}

// --- pattern-choice: tool_choice on this gateway ---------------------------------------

export const CHOICES = ['auto', 'required', 'none', 'named'] as const;
export type Choice = (typeof CHOICES)[number];
export const choiceLabels: Record<Choice, string> = {
  auto: 'auto',
  required: 'required',
  none: 'none',
  named: 'the name list_tasks',
};
export const toChoice = (value: string): Choice => CHOICES.find((id) => id === value) ?? 'auto';
export const CHOICE_QUESTION = 'What is open in Apollo?';
export const LEAKED_TEXT = '<tool_call>list_projects</tool_call>';
export interface ChoiceReply {
  /** The tool_choice field as sent. */
  sent: string;
  finishReason: 'tool_calls' | 'stop';
  /** The tool called, or null when the reply was text. */
  tool: string | null;
  /** The text content, or null when the reply was a tool call. */
  text: string | null;
  honored: string;
  message: string;
}
export const choiceReplies: Record<Choice, ChoiceReply> = {
  auto: {
    sent: '"auto"',
    finishReason: 'tool_calls',
    tool: 'list_projects',
    text: null,
    honored: 'honored: the model chose a tool on its own',
    message: 'The model chose list_projects on its own, which auto allows.',
  },
  required: {
    sent: '"required"',
    finishReason: 'tool_calls',
    tool: 'list_projects',
    text: null,
    honored: 'honored: a tool was called',
    message: 'A tool was required and list_projects was called, so required was honored.',
  },
  none: {
    sent: '"none"',
    finishReason: 'stop',
    tool: null,
    text: LEAKED_TEXT,
    honored: 'honored, with a leak: the call appeared as text',
    message: 'No tool ran, but the call leaked into the text for code to discard.',
  },
  named: {
    sent: '{"type": "function", "function": {"name": "list_tasks"}}',
    finishReason: 'tool_calls',
    tool: 'list_projects',
    text: null,
    honored: 'not honored: list_projects was called, not list_tasks',
    message: 'The named tool was ignored and list_projects was called instead.',
  },
};
export const requestText = (choice: Choice) =>
  `"messages": [{"role": "user", "content": "${CHOICE_QUESTION}"}],\n"tools": [… 13 tools …],\n"tool_choice": ${choiceReplies[choice].sent}`;
export const replyText = (choice: Choice) => {
  const reply = choiceReplies[choice];
  return reply.tool === null
    ? `finish_reason: ${reply.finishReason}\ncontent: ${reply.text ?? ''}`
    : `finish_reason: ${reply.finishReason}\ntool_calls: ${reply.tool}({})`;
};
export const cameBackText = (reply: ChoiceReply) =>
  reply.tool === null ? `text: ${reply.text ?? ''}` : `tool call: ${reply.tool}`;
export interface ChoiceState {
  choice: Choice;
  sent: boolean;
}
export const initialChoice = (): ChoiceState => ({ choice: 'auto', sent: false });
export type ChoiceAction = { type: 'choice'; value: Choice } | { type: 'send' } | { type: 'reset' };
export function choiceTransition(state: ChoiceState, action: ChoiceAction): ChoiceState {
  switch (action.type) {
    case 'reset':
      return initialChoice();
    case 'choice':
      return { choice: action.value, sent: false };
    case 'send':
      return { ...state, sent: true };
  }
}
export const CHOICE_START_MESSAGE = 'Choose a tool_choice value, then press Send.';
export const CHOICE_REFINEMENT =
  'Refinement: use the two values the provider honors, and check the rest.';
export function choiceSummary(state: ChoiceState): Summary {
  if (!state.sent) return { tone: 'neutral', message: CHOICE_START_MESSAGE };
  const reply = choiceReplies[state.choice];
  const tone = reply.honored.startsWith('honored:') ? 'success' : 'warning';
  return { tone, message: reply.message };
}
